/**
 * When each market-session chime is due.
 *
 * Deliberately pure and separated from the React component: this is the logic that
 * decides whether a sound fires, and getting it wrong is the difference between a
 * closing bell at 16:00 and a closing bell at some arbitrary moment the next morning.
 * Keeping it here makes it directly testable (see marketChimeSchedule.test.ts).
 *
 * Nothing in this module reads the clock itself — callers pass `nowMs` — so tests can
 * drive it deterministically.
 */
import type { ChimeName } from "./chimeAudio";
import type { SessionBounds } from "./marketSessionClock";

export type CueId = "open" | "warn5" | "warn2" | "countdown";

export interface Cue {
  cue: CueId;
  /** Instant the cue should sound. */
  atMs: number;
  sound: ChimeName;
}

/**
 * How late a discrete cue may still fire, on the FALLBACK path only.
 *
 * The primary path queues cues onto the audio clock ahead of time (see CUE_LEAD_MS).
 * This window only covers the case where we arrive after the instant has passed —
 * a page opened or sound armed moments too late. Past it we stay silent rather than
 * chiming at the wrong time.
 */
export const TOLERANCE_MS = 20_000;

/**
 * How far ahead a discrete cue is handed to the audio clock.
 *
 * This is what makes the warnings survive a buried tab. The supervisor interval is
 * throttled in a background tab — to ~1/s normally, and as bad as ~1/min under
 * Chrome's intensive throttling — so relying on a tick landing inside a 20-second
 * tolerance window was a real gap: a once-a-minute tick can step straight over it and
 * the 15:55 warning never fires.
 *
 * A 90-second lead is wider than the worst throttling interval, so at least one tick
 * is guaranteed to land inside the window. Once queued, the audio thread keeps the
 * time and throttling is irrelevant. Same mechanism the countdown already used.
 */
export const CUE_LEAD_MS = 90_000;

/**
 * How far ahead the countdown is handed to the audio clock.
 *
 * Generous on purpose. The supervisor interval is throttled in a background tab (to
 * ~1/s normally, and as bad as ~1/min under Chrome's intensive throttling), so we want
 * the whole sequence queued well before it is needed. Once queued on the audio thread,
 * throttling cannot affect its timing at all.
 */
export const COUNTDOWN_LEAD_MS = 65_000;

export const WARN_5_MS = 5 * 60_000;
export const WARN_2_MS = 2 * 60_000;

/**
 * The three discrete cues for a session, in chronological order.
 * Empty when the market is closed that day.
 *
 * Note the warnings are defined relative to the CLOSE, not hard-coded to 15:55/15:58,
 * so a 13:00 half day automatically warns at 12:55 and 12:58.
 */
export function discreteCues(bounds: SessionBounds): Cue[] {
  if (bounds.openMs == null || bounds.closeMs == null) return [];
  return [
    { cue: "open", atMs: bounds.openMs, sound: "open-bell" },
    { cue: "warn5", atMs: bounds.closeMs - WARN_5_MS, sound: "warn-5min" },
    { cue: "warn2", atMs: bounds.closeMs - WARN_2_MS, sound: "warn-2min" },
  ];
}

/**
 * Which discrete cues should be QUEUED onto the audio clock now — i.e. those whose
 * instant is still ahead of us but inside the lead window.
 *
 * This is the primary path. Callers must queue each cue only once per session; this
 * function has no memory.
 */
export function cuesToQueue(
  nowMs: number,
  bounds: SessionBounds,
  leadMs: number = CUE_LEAD_MS,
): Cue[] {
  return discreteCues(bounds).filter((c) => nowMs >= c.atMs - leadMs && nowMs < c.atMs);
}

/**
 * Which discrete cues are already past but still within tolerance — the FALLBACK path,
 * for when we arrive too late to have queued them (page just opened, sound just armed).
 *
 * A cue qualifies only inside [atMs, atMs + tolerance). The upper bound is what stops a
 * dashboard opened at 11:00 from replaying the opening bell, and stops a laptop waking
 * from sleep at 18:00 from firing the whole day's sequence at once.
 */
export function dueDiscreteCues(
  nowMs: number,
  bounds: SessionBounds,
  toleranceMs: number = TOLERANCE_MS,
): Cue[] {
  return discreteCues(bounds).filter((c) => nowMs >= c.atMs && nowMs - c.atMs < toleranceMs);
}

/**
 * Whether the countdown + closing bell should be queued onto the audio clock now.
 *
 * True only in the lead window before the close. Callers must additionally ensure they
 * queue it once per session — this function has no memory.
 */
export function shouldQueueCountdown(
  nowMs: number,
  bounds: SessionBounds,
  leadMs: number = COUNTDOWN_LEAD_MS,
): boolean {
  if (bounds.closeMs == null) return false;
  return nowMs >= bounds.closeMs - leadMs && nowMs < bounds.closeMs;
}

/**
 * Fallback: the close has already passed but the countdown was never queued (the tab
 * was asleep or the sound was armed a moment too late). Ring the bell alone, if we are
 * still within tolerance of the close.
 */
export function shouldRingCloseAlone(
  nowMs: number,
  bounds: SessionBounds,
  countdownQueued: boolean,
  toleranceMs: number = TOLERANCE_MS,
): boolean {
  if (bounds.closeMs == null || countdownQueued) return false;
  return nowMs >= bounds.closeMs && nowMs - bounds.closeMs < toleranceMs;
}
